import { userContext } from './catalog';
import type { CatalogQueryResult } from './catalogQuery';
import type { PolicyAsset, SyntheticUser } from './types';

const TOKEN_RE = /\$\{([A-Za-z_][A-Za-z0-9_.]*)\}/g;
const IDENT_PART = '(?:"[A-Za-z_][A-Za-z0-9_]*"|[A-Za-z_][A-Za-z0-9_]*)';
const QUALIFIED_RE = new RegExp(`${IDENT_PART}(?:\\.${IDENT_PART})+`, 'g');
const FROM_RE = new RegExp(`\\bFROM\\s+(${IDENT_PART}(?:\\.${IDENT_PART})*)`, 'i');
const FROM_ALIAS_RE = new RegExp(
  `\\b(?:FROM|JOIN)\\s+(${IDENT_PART}(?:\\.${IDENT_PART})*)(?:\\s+(?:AS\\s+)?(${IDENT_PART}))?`,
  'ig',
);
const SQL_KEYWORDS = new Set([
  'where', 'join', 'left', 'right', 'full', 'inner', 'cross', 'outer', 'on', 'limit',
  'group', 'order', 'having', 'union', 'as', 'select', 'from', 'and', 'or', 'not',
]);
const READONLY_HEADS = new Set(['select', 'with', 'show', 'describe', 'desc', 'explain', 'values']);
const WRITE_SQL = /\b(insert|update|delete|drop|alter|truncate|merge|upsert|call|execute|exec|grant|revoke|create|replace|copy|unload|vacuum|refresh|comment|attach|detach|begin|commit|rollback|start|declare|prepare|deallocate|kill|use|set|reset|load|import|export|overwrite|into)\b/i;
const FOR_UPDATE = /\bfor\s+(update|share)\b/i;
export const READONLY_SQL_ERROR = 'Only read-only SELECT queries are allowed';

function stripSqlComments(sql: string): string {
  return sql
    .replace(/\/\*[\s\S]*?\*\//g, ' ')
    .replace(/--[^\n]*/g, ' ');
}

function maskSqlLiterals(sql: string): string {
  return sql.replace(/'(?:[^'\\]|\\.|'')*'/g, "''");
}

export function assertReadonlySql(sql: string): string | undefined {
  const stripped = stripSqlComments((sql || '').trim()).trim();
  if (!stripped) return 'Query is required';
  const statements = stripped.split(';').map((part) => part.trim()).filter(Boolean);
  if (statements.length !== 1) return 'Only one SQL statement is allowed';
  const probe = maskSqlLiterals(statements[0]);
  const head = probe.trim().match(/^([a-z]+)/i)?.[1]?.toLowerCase() ?? '';
  if (!READONLY_HEADS.has(head)) return READONLY_SQL_ERROR;
  if (head === 'with' && !/\bselect\b/i.test(probe)) return READONLY_SQL_ERROR;
  if (WRITE_SQL.test(probe) || FOR_UPDATE.test(probe)) return READONLY_SQL_ERROR;
  return undefined;
}

export function sqlStringLiteral(value: string): string {
  return `'${value.replace(/'/g, "''")}'`;
}

export function sqlClaimLiteral(value: unknown): string | null {
  if (value == null) return null;
  if (Array.isArray(value)) {
    const parts = value.map((item) => String(item).trim()).filter(Boolean);
    if (!parts.length) return null;
    return parts.map(sqlStringLiteral).join(', ');
  }
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  const text = String(value).trim();
  if (!text) return null;
  return sqlStringLiteral(text);
}

export function substituteClaimTokens(
  sql: string,
  claims: Record<string, unknown>,
): { sql: string; tokens: string[]; error?: string } {
  const tokens: string[] = [];
  const replaced = (sql || '').replace(TOKEN_RE, (_full, path: string) => {
    tokens.push(path);
    return sqlClaimLiteral(claims[path]) ?? `\${${path}}`;
  });
  const leftover = replaced.match(TOKEN_RE);
  if (leftover?.length) {
    return { sql: replaced, tokens, error: `Unknown or empty claim ${leftover[0]}` };
  }
  return { sql: replaced, tokens };
}

export function substituteUserClaims(sql: string, user: SyntheticUser) {
  return substituteClaimTokens(sql, userContext(user));
}

export function splitQualifiedName(raw: string): string[] {
  return (raw || '')
    .split('.')
    .map((part) => part.trim().replace(/^"|"$/g, ''))
    .filter(Boolean);
}

export function joinQualifiedName(parts: string[]): string {
  return parts
    .map((part) => (/^[A-Za-z_][A-Za-z0-9_]*$/.test(part) ? part : `"${part}"`))
    .join('.');
}

export function shortenQualifiedParts(parts: string[], catalog = ''): string[] {
  const next = parts.map((part) => part.trim()).filter(Boolean);
  const catalogName = catalog.trim().toLowerCase();
  while (next.length > 2 && next[0].toLowerCase() !== catalogName) {
    next.shift();
  }
  return next;
}

export function fromTableName(sql: string): string {
  const raw = sql.match(FROM_RE)?.[1] ?? '';
  return joinQualifiedName(splitQualifiedName(raw));
}

export function assetMatchesRelation(asset: PolicyAsset, relation: string): boolean {
  const needle = splitQualifiedName(relation).join('.').toLowerCase();
  if (!needle) return false;
  const name = asset.name.toLowerCase();
  const path = splitQualifiedName(asset.path || '').join('.').toLowerCase();
  const last = path.split('.').filter(Boolean).pop() || '';
  return needle === name
    || needle === asset.id.toLowerCase()
    || needle === path
    || needle === last
    || needle.endsWith(`.${name}`)
    || Boolean(path && path.endsWith(`.${needle}`));
}

function findAssetForRelation(relation: string, assets: PolicyAsset[]): PolicyAsset | undefined {
  return assets.find((asset) => assetMatchesRelation(asset, relation));
}

export function resolveLiveAsset(sql: string, assets: PolicyAsset[]): { asset?: PolicyAsset; error?: string } {
  const name = fromTableName(sql);
  if (!name) return { error: 'FROM requires a table name.' };
  const asset = findAssetForRelation(name, assets);
  if (!asset) return { error: `Table ${name} is not in this policy scope.` };
  if (!asset.sourceId?.trim()) return { error: `${asset.name} has no live connection.` };
  return { asset };
}

function rewriteOutsideStrings(sql: string, rewrite: (chunk: string) => string): string {
  const out: string[] = [];
  let index = 0;
  while (index < sql.length) {
    if (sql[index] === "'") {
      let end = index + 1;
      while (end < sql.length) {
        if (sql[end] === "'" && sql[end + 1] === "'") {
          end += 2;
          continue;
        }
        if (sql[end] === "'") {
          end += 1;
          break;
        }
        end += 1;
      }
      out.push(sql.slice(index, end));
      index = end;
      continue;
    }
    let end = index;
    while (end < sql.length && sql[end] !== "'") end += 1;
    out.push(rewrite(sql.slice(index, end)));
    index = end;
  }
  return out.join('');
}

function tableAliases(sql: string, assets: PolicyAsset[]): Map<string, string> {
  const aliases = new Map<string, string>();
  rewriteOutsideStrings(sql, (chunk) => {
    FROM_ALIAS_RE.lastIndex = 0;
    let match: RegExpExecArray | null = FROM_ALIAS_RE.exec(chunk);
    while (match) {
      const relation = splitQualifiedName(match[1] || '').join('.');
      const aliasRaw = (match[2] || '').replace(/^"|"$/g, '');
      const alias = aliasRaw && !SQL_KEYWORDS.has(aliasRaw.toLowerCase()) ? aliasRaw : '';
      const asset = findAssetForRelation(relation, assets);
      const table = asset?.name || splitQualifiedName(relation).slice(-1)[0] || '';
      if (alias && table) {
        aliases.set(table.toLowerCase(), alias);
        aliases.set(relation.toLowerCase(), alias);
        if (asset?.path) aliases.set(splitQualifiedName(asset.path).join('.').toLowerCase(), alias);
      }
      match = FROM_ALIAS_RE.exec(chunk);
    }
    return chunk;
  });
  return aliases;
}

export function rewriteLiveSql(sql: string, assets: PolicyAsset[], catalog = ''): string {
  const aliases = tableAliases(sql, assets);
  return rewriteOutsideStrings(sql, (chunk) => chunk.replace(QUALIFIED_RE, (raw) => {
    const parts = splitQualifiedName(raw);
    if (parts.length < 2) return raw;
    const asset = findAssetForRelation(parts.join('.'), assets)
      || (parts.length >= 3 ? findAssetForRelation(parts.slice(0, -1).join('.'), assets) : undefined);
    if (asset && parts.length >= 3) {
      const relationParts = splitQualifiedName(asset.path || asset.name);
      const isRelationOnly = parts.length === relationParts.length
        || parts.join('.').toLowerCase() === (asset.path || asset.name).toLowerCase();
      if (isRelationOnly) return asset.name;
      const column = parts[parts.length - 1];
      const alias = aliases.get(asset.name.toLowerCase())
        || aliases.get(splitQualifiedName(asset.path || '').join('.').toLowerCase());
      return joinQualifiedName([alias || asset.name, column]);
    }
    if (parts.length < 3) return raw;
    return joinQualifiedName(shortenQualifiedParts(parts, catalog));
  }));
}

export function liveQueryFail(message: string, extra: Partial<CatalogQueryResult> = {}): CatalogQueryResult {
  return {
    ok: false,
    errors: extra.errors?.length ? extra.errors : [message],
    columns: [],
    rows: [],
    matched: 0,
    scanned: 0,
    truncated: false,
    where: extra.where ?? '',
    explanation: message,
    ...extra,
  };
}
